import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  User as UserIcon, Mail, Phone, MapPin, Hash, Sparkles, Save, Lock,
  CreditCard, Building2, Briefcase, Smile,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import AvatarStudioCore from "@/components/AvatarStudioCore";

export default function WorkerProfilePage() {
  const { user, refresh } = useAuth();
  const [worker, setWorker] = useState(null);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", address: "",
    abn: "", bank_name: "", bsb: "", bank_account: "",
    intro: "", bio: "",
  });
  const [busy, setBusy] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = async () => {
    try {
      const { data } = await api.get("/workers");
      const me = data.find((w) => w.id === user?.worker_id) || data[0];
      setWorker(me);
      if (me) setForm({
        full_name: me.full_name || "", email: me.email || "",
        phone: me.phone || "", address: me.address || "",
        abn: me.abn || "", bank_name: me.bank_name || "",
        bsb: me.bsb || "", bank_account: me.bank_account || "",
        intro: me.intro || "", bio: me.bio || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { if (user?.worker_id) load(); }, [user]);

  const save = async () => {
    if (!worker) return;
    setBusy(true);
    try {
      await api.put(`/workers/${worker.id}`, form);
      toast.success("Profile saved (sent to client for approval)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!worker) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="worker-profile-page">
      <header>
        <h1 className="text-4xl font-display font-black tracking-tight">My Profile</h1>
        <p className="text-muted-foreground">Your contact, banking, and bio details. Changes are sent to the client for approval.</p>
      </header>

      {/* Hero */}
      <Card className="rounded-3xl p-6 sm:p-8 border-2 relative overflow-hidden card-lift" data-testid="worker-profile-hero">
        <div className="blob animate-blob" style={{ width: 240, height: 240, top: -80, right: -60, background: "hsl(var(--worker) / 0.30)" }} />
        <div className="blob animate-blob" style={{ width: 200, height: 200, bottom: -80, left: -40, background: "hsl(var(--coral) / 0.18)", animationDelay: "-7s" }} />
        <div className="relative z-10 flex flex-wrap items-center gap-5">
          <div className="relative">
            <div className="w-32 h-32 rounded-3xl overflow-hidden ring-4 ring-worker/40 bg-muted grid place-items-center" data-testid="worker-avatar-display">
              {worker.avatar_url
                ? <img src={worker.avatar_url} alt="me" className="w-full h-full object-cover" />
                : worker.photo_url
                  ? <img src={worker.photo_url} alt="me" className="w-full h-full object-cover" />
                  : <span className="text-5xl font-display font-black text-worker">{(form.full_name || "?")[0]}</span>}
            </div>
            <Button onClick={() => setAvatarOpen(true)} size="sm" data-testid="worker-change-avatar-btn"
              className="absolute -bottom-2 -right-2 rounded-full h-9 px-3 shadow-lg gap-1 font-bold text-white"
              style={{ background: "linear-gradient(135deg, hsl(var(--coral)), hsl(var(--sun)))" }}>
              <Sparkles className="w-3.5 h-3.5" /> Change
            </Button>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground font-bold">Support Worker</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="worker-profile-name">
              {form.full_name || "Add your name below"}
            </h2>
            <div className="flex flex-wrap gap-2 mt-3 text-sm">
              {form.abn && <span className="rounded-full px-3 py-1 bg-worker/15 text-worker-foreground font-bold border-2 border-worker/30" data-testid="worker-abn-pill"><Briefcase className="w-3 h-3 inline mr-1" /> ABN {form.abn}</span>}
              {form.email && <span className="rounded-full px-3 py-1 bg-muted font-semibold" data-testid="worker-email-pill"><Mail className="w-3 h-3 inline mr-1" /> {form.email}</span>}
            </div>
          </div>
        </div>
      </Card>

      {/* Contact + identity */}
      <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="worker-contact-card">
        <h2 className="font-display font-extrabold text-2xl flex items-center gap-2"><UserIcon className="w-5 h-5 text-worker" /> Contact details</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field icon={UserIcon} id="full_name" label="Full Name" required v={form.full_name} oc={upd("full_name")} />
          <Field icon={Mail} id="email" label="Email (sign-in)" v={form.email} disabled lock />
          <Field icon={Phone} id="phone" label="Phone" v={form.phone} oc={upd("phone")} />
          <Field icon={Briefcase} id="abn" label="ABN" v={form.abn} oc={upd("abn")} />
          <div className="sm:col-span-2"><Field icon={MapPin} id="address" label="Address" v={form.address} oc={upd("address")} /></div>
        </div>
      </Card>

      {/* Banking */}
      <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="worker-banking-card">
        <h2 className="font-display font-extrabold text-2xl flex items-center gap-2"><CreditCard className="w-5 h-5 text-worker" /> Banking details</h2>
        <p className="text-sm text-muted-foreground">These appear on every cover letter / invoice payment block.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field icon={Building2} id="bank_name" label="Bank Name" v={form.bank_name} oc={upd("bank_name")} placeholder="e.g. Commonwealth Bank" />
          <Field icon={Hash} id="bsb" label="BSB" v={form.bsb} oc={upd("bsb")} placeholder="123-456" />
          <Field icon={Hash} id="bank_account" label="Account Number" v={form.bank_account} oc={upd("bank_account")} />
        </div>
      </Card>

      {/* Bio + intro */}
      <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="worker-bio-card">
        <h2 className="font-display font-extrabold text-2xl flex items-center gap-2"><Smile className="w-5 h-5 text-worker" /> About me</h2>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase font-bold tracking-wider">Cover letter intro (used on every invoice)</Label>
          <Textarea value={form.intro || ""} onChange={upd("intro")} rows={3} className="rounded-xl"
            placeholder="A short sentence about your relationship with the client. e.g. Jamie has supported Sarah for 2 years with personal care and social support."
            data-testid="worker-intro-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase font-bold tracking-wider">Personal bio (private — for the client only)</Label>
          <Textarea value={form.bio || ""} onChange={upd("bio")} rows={4} className="rounded-xl"
            placeholder="Hobbies, languages spoken, anything friendly you'd like the client to know."
            data-testid="worker-bio-input" />
        </div>
      </Card>

      <div className="flex flex-wrap gap-2 justify-end items-center pt-2">
        <p className="text-xs text-muted-foreground mr-auto">Edits create a pending notification — the client must approve before the changes go live.</p>
        <Button onClick={save} disabled={busy} className="rounded-full font-bold gap-2 min-h-[48px] text-foreground"
          style={{ background: "hsl(var(--worker))" }} data-testid="worker-save-profile-btn">
          <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save profile"}
        </Button>
      </div>

      <Card className="rounded-3xl border-2 border-rose-300/40 p-5 bg-rose-500/5 mt-6" data-testid="worker-leave-card">
        <h3 className="font-display font-extrabold text-lg text-rose-700 dark:text-rose-300">Leave this engagement</h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          If you no longer support this client, you can request to be removed from their worker list.
          The client must approve before your engagement is closed. Your past timesheets and shift notes are kept
          on the client's record.
        </p>
        <Button
          onClick={async () => {
            if (!window.confirm(`Send a request to ${worker.full_name ? `your client` : "the client"} to remove yourself from their worker list?`)) return;
            try {
              const r = await api.delete(`/workers/${worker.id}`);
              if (r.data?.pending_approval) toast.success("Request sent. Your client will need to approve.");
              else toast.success("Removed.");
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          variant="outline"
          className="mt-4 rounded-full font-bold border-rose-400 text-rose-700 hover:bg-rose-100 dark:text-rose-200 dark:hover:bg-rose-900/30"
          data-testid="worker-request-leave-btn"
        >
          Request to leave
        </Button>
      </Card>

      <Dialog open={avatarOpen} onOpenChange={setAvatarOpen}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="worker-avatar-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-coral" /> Change my avatar</DialogTitle></DialogHeader>
          <AvatarStudioCore
            endpoints={{
              put: `/workers/${worker.id}/avatar`,
              aiGenerate: `/workers/${worker.id}/avatar/ai-generate`,
              aiEdit: `/workers/${worker.id}/avatar/ai-edit`,
            }}
            currentUrl={worker.avatar_url || ""}
            seedName={form.full_name || "Worker"}
            onChange={() => { load(); refresh(); }}
            compact
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ icon: Icon, id, label, v, oc, type = "text", placeholder, disabled, lock, required }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-worker" /> {label} {required && <span className="text-coral">*</span>}
        {lock && <Lock className="w-3 h-3 text-muted-foreground ml-auto" />}
      </Label>
      <Input id={id} value={v || ""} onChange={oc} type={type} placeholder={placeholder} disabled={disabled}
        className={`min-h-[48px] rounded-xl ${disabled ? "bg-muted/50" : ""}`}
        data-testid={`worker-field-${id}`} />
    </div>
  );
}
